import React, { useState, useEffect } from 'react';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches || 
        window.navigator.standalone === true ||
        document.referrer.includes('android-app://')) {
      setIsInstalled(true);
      return;
    }

    // Enhanced install prompt handling
    const handleBeforeInstallPrompt = (e) => {
      console.log('PWA: Install prompt event triggered');
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt after a short delay for better UX
      setTimeout(() => {
        setShowInstallPrompt(true);
      }, 3000);
    };

    // Listen for app install success
    const handleAppInstalled = () => {
      console.log('PWA: App installed successfully');
      setIsInstalled(true);
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
      
      // Show success message
      if (window.toast) {
        window.toast({
          title: "App Installed!",
          description: "D.C. Tech Corp has been installed successfully.",
        });
      }
    };

    // Check for iOS devices and show iOS-specific install instructions
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    if (isIOS && !window.navigator.standalone) {
      setTimeout(() => {
        setShowInstallPrompt(true);
      }, 5000);
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // For iOS devices, show install instructions
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      if (isIOS) {
        // Better iOS install instructions
        const instructionMessage = `To install D.C. Tech Corp on your iPhone/iPad:\n\n1. Tap the Share button (□↗) in Safari\n2. Scroll down and tap "Add to Home Screen"\n3. Tap "Add" to install the app\n\nYou'll then be able to use our app like a native app with offline access!`;
        alert(instructionMessage);
        return;
      }
      return;
    }

    setIsInstalling(true);
    
    try {
      // Show the install prompt
      const promptResult = await deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      console.log('PWA: User choice:', outcome);
      
      if (outcome === 'accepted') {
        console.log('PWA: User accepted the install prompt');
        // Show success feedback
        if (window.toast) {
          window.toast({
            title: "Installing...",
            description: "D.C. Tech Corp is being installed on your device.",
          });
        }
      } else {
        console.log('PWA: User dismissed the install prompt');
      }
      
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
    } catch (error) {
      console.error('PWA: Install prompt error:', error);
      // Show error feedback
      if (window.toast) {
        window.toast({
          title: "Installation Issue",
          description: "There was an issue with the installation. Please try again.",
          variant: "destructive"
        });
      }
    } finally {
      setIsInstalling(false);
    }
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    // Don't show again for this session
    sessionStorage.setItem('pwa-install-dismissed', 'true');
  };

  // Don't show if already installed, dismissed, or no prompt available
  if (isInstalled || 
      (!showInstallPrompt && !deferredPrompt) || 
      sessionStorage.getItem('pwa-install-dismissed')) {
    return null;
  }

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  return (
    <div className="fixed bottom-4 left-4 right-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 rounded-lg shadow-xl z-50 max-w-sm mx-auto animate-slide-up">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center text-lg">
            📱
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold mb-1">Install D.C. Tech Corp</h3>
          <p className="text-xs opacity-90 mb-3 leading-relaxed">
            {isIOS 
              ? "Tap Share → Add to Home Screen for the best experience with offline access."
              : "Install our app for faster access, offline support, and push notifications."
            }
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleInstallClick}
              disabled={isInstalling}
              className="bg-white text-indigo-600 px-4 py-2 rounded-md text-xs font-medium hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              {isInstalling ? (
                <>
                  <div className="w-3 h-3 border border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                  Installing...
                </>
              ) : (
                <>
                  📲 {isIOS ? 'Instructions' : 'Install'}
                </>
              )}
            </button>
            <button
              onClick={handleDismiss}
              className="text-white opacity-75 hover:opacity-100 px-3 py-2 rounded-md text-xs transition-opacity"
            >
              Later
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 text-white opacity-75 hover:opacity-100 transition-opacity p-1"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;